import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, ApiError } from '../api/client.js';
import type { MeResponse } from '../api/types.js';
import { tenantStore } from '../state/tenant.js';

export function useMe() {
  return useQuery<MeResponse | null>({
    queryKey: ['me'],
    queryFn: async () => {
      try {
        const me = await api<MeResponse>('/api/v1/auth/me', { tenant: false });
        // Tenant-Auswahl initialisieren/korrigieren
        const slugs = me.tenants.map((tenant) => tenant.slug);
        const selected = tenantStore.get();
        if (slugs.length > 0 && (selected === null || !slugs.includes(selected))) {
          tenantStore.set(slugs[0] ?? null);
        }
        return me;
      } catch (error) {
        if (error instanceof ApiError && error.status === 401) return null;
        throw error;
      }
    },
    staleTime: 60_000,
    retry: false,
  });
}

export function useLogin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { email: string; password: string }) =>
      api<MeResponse>('/api/v1/auth/login', { method: 'POST', body: input, tenant: false }),
    onSuccess: (me) => {
      queryClient.setQueryData(['me'], me);
      tenantStore.set(me.tenants[0]?.slug ?? null);
    },
  });
}

export function useLogout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api<{ ok: boolean }>('/api/v1/auth/logout', { method: 'POST', tenant: false }),
    onSettled: () => {
      queryClient.setQueryData(['me'], null);
      queryClient.clear();
    },
  });
}
